import { useState, useEffect, useCallback, useRef } from "react";
import { useVoiceCall } from "./useVoiceCall";
import { useLiveKitCall } from "./useLiveKitCall";
import { isLiveKitConfigured } from "../utils/livekitApi";
export function useCall({ deviceId, isConnected, sendCallSignal, canReceiveFile, walletAddress, guestToken, deviceSessionToken, signWalletMessage, getE2EEKeyMaterial, e2eeKeyAvailable, }) {
    // null = probe not finished yet; then 'livekit' | 'mesh' for the session.
    const [backend, setBackend] = useState(null);
    const backendRef = useRef(null);
    backendRef.current = backend;
    /** lk-* signals arriving before the probe finishes are buffered, not lost —
     *  otherwise an incoming SFU ring during app startup would be missed. */
    const pendingLkSignalsRef = useRef([]);
    useEffect(() => {
        if (!walletAddress)
            return;
        let cancelled = false;
        isLiveKitConfigured().then((ok) => {
            if (!cancelled)
                setBackend(ok ? "livekit" : "mesh");
        });
        return () => {
            cancelled = true;
        };
    }, [walletAddress]);
    const mesh = useVoiceCall({ deviceId, isConnected, sendCallSignal, canReceiveFile });
    const livekit = useLiveKitCall({
        deviceId,
        isConnected,
        sendCallSignal,
        canReceiveFile,
        roomName: walletAddress ? walletAddress.toLowerCase() : null,
        guestToken,
        deviceSessionToken,
        signWalletMessage,
        getE2EEKeyMaterial,
        e2eeKeyAvailable,
        enabled: backend === "livekit",
    });
    const meshRef = useRef(mesh);
    const livekitRef = useRef(livekit);
    meshRef.current = mesh;
    livekitRef.current = livekit;
    /** Route incoming signals by subtype: 'lk-*' → LiveKit, rest → mesh. This
     *  also keeps mixed-version rooms working (old bundles simply ignore lk-*). */
    const handleSignal = useCallback((signal) => {
        if (signal.subtype?.startsWith("lk-")) {
            if (backendRef.current === null) {
                pendingLkSignalsRef.current.push(signal);
            }
            else if (backendRef.current === "livekit") {
                livekitRef.current.handleSignal(signal);
            }
        }
        else {
            meshRef.current.handleSignal(signal);
        }
    }, []);
    // replay buffered lk-* signals once the probe resolves
    useEffect(() => {
        if (backend === null)
            return;
        const pending = pendingLkSignalsRef.current;
        pendingLkSignalsRef.current = [];
        if (backend === "livekit")
            pending.forEach((s) => livekitRef.current.handleSignal(s));
    }, [backend]);
    /** Until the probe finishes, defer startCall briefly instead of guessing. */
    const startCall = useCallback(async (opts) => {
        let b = backendRef.current;
        if (b === null) {
            for (let i = 0; i < 20 && b === null; i++) {
                await new Promise((r) => setTimeout(r, 250));
                b = backendRef.current;
            }
            b = b ?? "mesh";
        }
        if (b === "livekit")
            return livekitRef.current.startCall(opts);
        return meshRef.current.startCall(opts);
    }, []);
    const shareFile = useCallback((file, mode) => {
        // whichever backend is in a call owns the transfer
        if (livekitRef.current.callState !== "idle")
            return livekitRef.current.shareFile(file, mode);
        return meshRef.current.shareFile(file, mode);
    }, []);
    /** Explicit departure differs from a temporary hangup: clear only the
     * selected call transport's rejoin/media state, never the guest session. */
    const leaveCall = useCallback(() => {
        if (livekitRef.current.callState !== "idle") {
            livekitRef.current.leaveCall();
        }
        else {
            meshRef.current.leaveCall();
        }
    }, []);
    // The active surface: LiveKit when selected, mesh otherwise. Incoming mesh
    // calls (old-bundle callers) still ring even in LiveKit mode — expose
    // whichever backend is currently non-idle.
    const lkActive = backend === "livekit" && (livekit.callState !== "idle" || mesh.callState === "idle");
    const active = lkActive ? livekit : mesh;
    const rejoinOwner = active.callState === "idle" && active.canRejoin
        ? active
        : active.callState === "idle" && livekit.callState === "idle" && livekit.canRejoin
            ? livekit
            : active.callState === "idle" && mesh.callState === "idle" && mesh.canRejoin
                ? mesh
                : null;
    /** Discard an idle transport's recovery descriptor without touching an
     * unrelated active call on the other transport. Prefer the descriptor the
     * public surface exposes when both transports happen to have one. */
    const dismissRejoin = useCallback(() => {
        const recovery = active.callState === "idle" && active.canRejoin
            ? active
            : livekit.callState === "idle" && livekit.canRejoin
                ? livekit
                : mesh.callState === "idle" && mesh.canRejoin
                    ? mesh
                    : null;
        recovery?.leaveCall();
    }, [active, livekit, mesh]);
    return {
        ...active,
        canRejoin: !!rejoinOwner,
        rejoinCall: rejoinOwner?.rejoinCall ?? active.rejoinCall,
        startCall,
        leaveCall,
        dismissRejoin,
        handleSignal,
        shareFile,
        callError: livekit.callError,
        dismissCallError: livekit.dismissCallError,
        /** Automatic TURN rescue only applies to the LiveKit backend. */
        connectionRecovery: lkActive ? livekit.connectionRecovery : null,
        // Descriptor must come from the same transport whose rejoin affordance is
        // exposed. Never pair mesh rejoinCall with stale LiveKit recovery data.
        rejoinCallIdentity: rejoinOwner?.rejoinCallIdentity ?? null,
        rejoinCallId: rejoinOwner?.rejoinCallId ?? null,
        /** Frame-E2EE state of the current LiveKit call; mesh (P2P) calls are
         *  direct DTLS-SRTP between peers, flagged separately as 'p2p'. */
        callE2EE: lkActive ? livekit.e2eeActive : mesh.callState !== "idle" ? "p2p" : null,
        /** Which transport this session uses (null while probing). */
        callBackend: backend,
        /** Mesh may report secure only after every current peer's WebRTC transport
         *  has reached `connected`; the UI call state alone is not transport proof. */
        connectionPhase: (lkActive
            ? livekit.connectionPhase
            : mesh.callState === "in-call" && mesh.transportConnected
                ? "secure"
                : mesh.callState === "in-call"
                    ? "encrypting"
                    : "dialing"),
    };
}
